/**
 * Email Manager - Frontend JavaScript
 */

(function () {
	'use strict';

	const searchTimers = new WeakMap();
	
	// Get token parameters from URL if present
	const urlParams = new URLSearchParams(window.location.search);
	const emailToken = urlParams.get('email_token');
	const authToken = urlParams.get('auth');

	document.addEventListener('DOMContentLoaded', function () {
		const managers = document.querySelectorAll('.xtra-email-manager');

		managers.forEach(initializeManager);
		checkEmptyState();
		reorganizeForMobile();
	});

	function initializeManager(manager) {
		// Set up search inputs (authors and communities)
		const searchInputs = manager.querySelectorAll('[data-search-type]');
		if (searchInputs.length) {
			searchInputs.forEach((input) => {
				input.addEventListener('input', handleSearch);
				input.addEventListener('focus', () => showSearchResults(input));
			});
			document.addEventListener('click', handleClickOutside);
		}

		// Set up unsubscribe buttons
		manager.querySelectorAll('.xtra-unsubscribe-button').forEach((button) => {
			button.addEventListener('click', handleUnsubscribe);
		});

		// Set up subscribe buttons
		manager.querySelectorAll('.xtra-subscribe-button').forEach((button) => {
			button.addEventListener('click', handleSubscribe);
		});

		// Set up pause all button
		const pauseButton = manager.querySelector('.xtra-pause-all-button');
		if (pauseButton) {
			pauseButton.addEventListener('click', handlePauseAll);
		}

		// Set up unsubscribe all button
		const unsubscribeAllButton = manager.querySelector('.xtra-unsubscribe-all-button');
		if (unsubscribeAllButton) {
			unsubscribeAllButton.addEventListener('click', handleUnsubscribeAll);
		}
		
		// Set up email toggle switches
		manager.querySelectorAll('.xtra-email-toggle-input').forEach((toggle) => {
			toggle.addEventListener('change', handleEmailToggle);
		});

		// Set up feed toggle switches
		manager.querySelectorAll('.xtra-feed-toggle-input').forEach((toggle) => {
			toggle.addEventListener('change', handleFeedToggle);
		});
		
		// Set up auto-subscribe toggle
		const autoSubscribeToggle = manager.querySelector('.xtra-auto-subscribe-toggle');
		if (autoSubscribeToggle) {
			autoSubscribeToggle.addEventListener('change', handleAutoSubscribeToggle);
		}

		// Set up email list toggles
		manager.querySelectorAll('.xtra-email-list-toggle').forEach((toggle) => {
			toggle.addEventListener('change', handleEmailListToggle);
		});
	}

	async function handleSearch(e) {
		const input = e.target;
		const query = input.value.trim();
		const wrapper = input.closest('.xtra-email-manager__search');
		const resultsContainer = wrapper?.querySelector('.xtra-search-results') || input.nextElementSibling;
		const searchType = input.dataset.searchType || 'author';
		const config = getSearchConfig(searchType);

		if (!resultsContainer) {
			return;
		}

		const existingTimer = searchTimers.get(input);
		if (existingTimer) {
			clearTimeout(existingTimer);
			searchTimers.delete(input);
		}

		if (query.length < 2) {
			resultsContainer.style.display = 'none';
			resultsContainer.innerHTML = '';
			searchTimers.delete(input);
			return;
		}

		// Show loading state
		resultsContainer.innerHTML = `<div class="xtra-search-loading">${config.loading}</div>`;
		resultsContainer.style.display = 'block';
		resultsContainer.dataset.resultsFor = searchType;

		const timer = setTimeout(async () => {
			try {
				const response = await fetch(
					`${config.endpoint}?search=${encodeURIComponent(query)}`,
					{
						headers: {
							'X-WP-Nonce': window.XXRest?.nonce,
						},
					}
				);

				const data = await response.json();
				const results = data.results || [];

				if (results.length > 0) {
					displaySearchResults(results, resultsContainer, searchType);
				} else {
					resultsContainer.innerHTML = `<div class="xtra-search-empty">${config.empty}</div>`;
				}
			} catch (error) {
				console.error('Search error:', error);
				resultsContainer.innerHTML = `<div class="xtra-search-error">${config.error}</div>`;
			} finally {
				searchTimers.delete(input);
			}
		}, 300);

		searchTimers.set(input, timer);
	}

	function displaySearchResults(results, container, type) {
		container.innerHTML = results
			.map((item) => (type === 'community' ? renderCommunityResult(item) : renderAuthorResult(item)))
			.join('');
		container.style.display = 'block';

		// Add click handlers to subscribe buttons
		container.querySelectorAll('.xtra-search-result__button').forEach((button) => {
			button.addEventListener('click', handleSearchResultClick);
		});
	}

	function renderAuthorResult(author) {
		const subscribed = !!author.is_subscribed;
		const buttonClass = subscribed ? 'button button-small xtra-search-result__button' : 'button button-small button-primary xtra-search-result__button';
		const avatarUrl = author.avatar || 'https://secure.gravatar.com/avatar/?s=96&d=mp';
		return `
			<div class="xtra-search-result" data-result-type="author" data-target-id="${escapeHtml(author.id)}">
				<img src="${escapeHtml(avatarUrl)}" alt="${escapeHtml(author.name)}" class="xtra-search-result__avatar">
				<div class="xtra-search-result__info">
					<div class="xtra-search-result__name">${escapeHtml(author.name)}</div>
					<div class="xtra-search-result__meta">
						${escapeHtml(formatFollowerMeta(author.post_count, author.subscriber_count))}
					</div>
				</div>
				<button class="${buttonClass}"
					data-subscription-type="author"
					data-target-id="${escapeHtml(author.id)}"
					data-target-name="${escapeHtml(author.name)}"
					data-avatar="${escapeHtml(avatarUrl)}"
					data-post-count="${escapeHtml(author.post_count)}"
					data-subscriber-count="${escapeHtml(author.subscriber_count)}"
					data-subscribed="${subscribed}">
					${subscribed ? 'Following' : 'Follow'}
				</button>
			</div>
		`;
	}

	function renderCommunityResult(community) {
		const subscribed = !!community.is_subscribed;
		const buttonClass = subscribed ? 'button button-small xtra-search-result__button' : 'button button-small button-primary xtra-search-result__button';
		return `
			<div class="xtra-search-result" data-result-type="community" data-target-id="${escapeHtml(community.id)}">
				<div class="xtra-search-result__avatar xtra-search-result__avatar--community">
					<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
						<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
						<circle cx="12" cy="10" r="3" />
					</svg>
				</div>
				<div class="xtra-search-result__info">
					<div class="xtra-search-result__name">${escapeHtml(community.name)}</div>
					<div class="xtra-search-result__meta">
						${escapeHtml(formatFollowerMeta(community.post_count, community.subscriber_count))}
					</div>
				</div>
				<button class="${buttonClass}"
					data-subscription-type="community"
					data-target-id="${escapeHtml(community.id)}"
					data-target-name="${escapeHtml(community.name)}"
					data-post-count="${escapeHtml(community.post_count)}"
					data-subscriber-count="${escapeHtml(community.subscriber_count)}"
					data-description="${escapeHtml(community.description || '')}"
					data-subscribed="${subscribed}">
					${subscribed ? 'Following' : 'Follow'}
				</button>
			</div>
		`;
	}

	function showSearchResults(input) {
		const results = input.nextElementSibling;
		if (results.innerHTML.trim()) {
			results.style.display = 'block';
		}
	}

	function handleClickOutside(e) {
		if (!e.target.closest('.xtra-email-manager__search')) {
			document.querySelectorAll('.xtra-search-results').forEach((results) => {
				results.style.display = 'none';
			});
		}
	}

	async function handleSearchResultClick(e) {
		const button = e.currentTarget;
		const subscriptionType = button.dataset.subscriptionType || 'author';
		const isSubscribed = button.dataset.subscribed === 'true';

		if (isSubscribed) {
			return;
		}

		const result = await handleSubscribe(e);
		if (!result?.success || (subscriptionType === 'community' && result.subscribed === false)) {
			return;
		}

		// Update button state
		button.textContent = 'Following';
		button.classList.remove('button-primary');
		button.dataset.subscribed = 'true';

		const manager = button.closest('.xtra-email-manager');
		const targetId = button.dataset.targetId;
		if (!manager || !targetId) {
			return;
		}

		const data = {
			id: parseInt(targetId, 10),
			name: button.dataset.targetName || '',
			avatar: button.dataset.avatar || '',
			postCount: parseInt(button.dataset.postCount || '0', 10),
			subscriberCount: parseInt(button.dataset.subscriberCount || '0', 10),
			description: button.dataset.description || '',
		};

		appendSubscriptionRow(manager, subscriptionType, data);

		const searchWrapper = button.closest('.xtra-email-manager__search');
		if (searchWrapper) {
			const input = searchWrapper.querySelector('input[data-search-type]');
			if (input) {
				input.value = '';
			}
			const resultsContainer = searchWrapper.querySelector('.xtra-search-results');
			if (resultsContainer) {
				resultsContainer.style.display = 'none';
			}
		}
	}

	async function handleSubscribe(e) {
		const button = e.currentTarget;
		const subscriptionType = button.dataset.subscriptionType || (button.dataset.communityId ? 'community' : 'author');
		const targetId = button.dataset.targetId || button.dataset.authorId || button.dataset.communityId;
		const targetName = button.dataset.targetName || button.dataset.authorName || '';
		const originalText = button.textContent;

		if (!targetId) {
			return { success: false };
		}

		button.disabled = true;
		button.textContent = 'Processing...';

		try {
			const headers = {
				'Content-Type': 'application/json',
			};
			if (window.XXRest?.nonce) {
				headers['X-WP-Nonce'] = window.XXRest.nonce;
			}

			const body = {};
			let endpoint;
			if (subscriptionType === 'community') {
				endpoint = '/wp-json/xtra/v1/subscriptions/communities/toggle';
				body.community_id = parseInt(targetId, 10);
			} else {
				endpoint = '/wp-json/xtra/v1/subscriptions/subscribe';
				body.author_id = parseInt(targetId, 10);
			}

			// Add token parameters if present
			if (emailToken && authToken) {
				body.email_token = emailToken;
				body.auth = authToken;
			}

			const response = await fetch(endpoint, {
				method: 'POST',
				headers,
				body: JSON.stringify(body),
			});

			const data = await response.json();

			if (data.success) {
				const subscribed = subscriptionType === 'community' ? !!data.subscribed : true;
				if (!subscribed) {
					button.textContent = originalText;
					showToast(data.message || `We couldn't follow ${targetName || subscriptionType}.`, 'error');
					return {
						success: false,
						subscriptionType,
						targetId: parseInt(targetId, 10),
						targetName,
						subscribed,
					};
				}

				const successMessage =
					data.message ||
					(subscriptionType === 'community'
						? `Following ${targetName || 'this community'}.`
						: `Subscribed to ${targetName || 'this author'}.`);
				showToast(successMessage, 'success');

				if (!button.classList.contains('xtra-search-result__button')) {
					button.textContent = subscriptionType === 'community' ? 'Following' : 'Subscribed';
				}

				// If this is a suggestion card, update it to subscription card
				const card = button.closest('.xtra-subscription-card--suggestion');
				if (card) {
					card.classList.remove('xtra-subscription-card--suggestion');
					button.textContent = 'Remove';
					button.classList.remove('button-primary', 'xtra-subscribe-button');
					button.classList.add('xtra-unsubscribe-button');
					button.removeEventListener('click', handleSubscribe);
					button.addEventListener('click', handleUnsubscribe);
					card.dataset.subscriptionType = subscriptionType;
					card.dataset.targetId = String(parseInt(targetId, 10));
					card.dataset.targetName = targetName;

					// Move to subscriptions list
					moveToSubscriptions(card);
					updateSectionCounts();
					checkEmptyState();
				}

				return {
					success: true,
					subscriptionType,
					targetId: parseInt(targetId, 10),
					targetName,
					subscribed: true,
				};
			}

			button.textContent = originalText;
			const typeLabel = subscriptionType === 'community' ? 'community' : 'author';
			showToast(data.message || `We couldn't follow ${targetName || `this ${typeLabel}`}.`, 'error');
			return {
				success: false,
				subscriptionType,
				targetId: parseInt(targetId, 10),
				targetName,
				subscribed: false,
			};
		} catch (error) {
			console.error('Subscribe error:', error);
			button.textContent = originalText;
			const typeLabel = subscriptionType === 'community' ? 'community' : 'author';
			showToast(`We couldn't follow ${targetName || `this ${typeLabel}`}. Please try again.`, 'error');
			return {
				success: false,
				subscriptionType,
				targetId: parseInt(targetId, 10),
				targetName,
				subscribed: false,
			};
		} finally {
			button.disabled = false;
		}
	}

	async function handleUnsubscribe(e) {
		const button = e.currentTarget;
		const subscriptionType = button.dataset.subscriptionType || 'author';
		const targetId = button.dataset.targetId;
		const targetName = button.dataset.targetName;
		const typeLabel = subscriptionType === 'community' ? 'community' : 'author';
		const originalText = button.textContent;
		
		button.disabled = true;
		button.textContent = 'Processing...';
		
		try {
			let endpoint, body;
			
			if (subscriptionType === 'community') {
				endpoint = '/wp-json/xtra/v1/subscriptions/communities/toggle';
				body = { community_id: parseInt(targetId) };
			} else {
				endpoint = '/wp-json/xtra/v1/subscriptions/unsubscribe';
				body = { author_id: parseInt(targetId) };
			}
			
			// Add token parameters if present
			if (emailToken && authToken) {
				body.email_token = emailToken;
				body.auth = authToken;
			}
			
			// Build headers - only add nonce if available
			const headers = {
				'Content-Type': 'application/json'
			};
			if (window.XXRest?.nonce) {
				headers['X-WP-Nonce'] = window.XXRest.nonce;
			}
			
			const response = await fetch(endpoint, {
				method: 'POST',
				headers: headers,
				body: JSON.stringify(body),
			});
			
			const data = await response.json();
			
			if (data.success) {
				showToast(data.message || `Unfollowed ${targetName || typeLabel}.`, 'success');

				// Remove row with animation
				const row = button.closest('.xtra-subscription-row');
				row.style.opacity = '0';
				row.style.transform = 'translateX(-20px)';
				setTimeout(() => {
					row.remove();
					updateSectionCounts();
					checkEmptyState();
				}, 300);
			} else {
				button.textContent = originalText;
				showToast(data.message || `We couldn't unfollow ${targetName || typeLabel}.`, 'error');
			}
		} catch (error) {
			console.error('Unsubscribe error:', error);
			button.textContent = originalText;
			showToast(`We couldn't unfollow ${targetName || typeLabel}. Please try again.`, 'error');
		} finally {
			button.disabled = false;
		}
	}

	async function handlePauseAll(e) {
		const button = e.currentTarget;
		const isPaused = button.dataset.paused === 'true';
		const action = isPaused ? 'resume-all' : 'pause-all';
		const originalText = button.textContent;

		button.disabled = true;
		button.textContent = 'Processing...';

		try {
			const response = await fetch(`/wp-json/xtra/v1/subscriptions/${action}`, {
				method: 'POST',
				headers: {
					'X-WP-Nonce': window.XXRest?.nonce,
				},
			});

			const data = await response.json();

			if (data.success) {
				showToast(data.message || (isPaused ? 'Email digest turned on.' : 'Email digest turned off.'), 'success');
				button.dataset.paused = isPaused ? 'false' : 'true';
				button.textContent = isPaused
					? 'Turn Off Email Digest'
					: 'Turn On Email Digest';
			} else {
				button.textContent = originalText;
				showToast(data.message || 'Unable to update email digest setting.', 'error');
			}
		} catch (error) {
			console.error('Pause/resume error:', error);
			button.textContent = originalText;
			showToast('Unable to update email digest setting. Please try again.', 'error');
		} finally {
			button.disabled = false;
		}
	}

	async function handleUnsubscribeAll(e) {
		const button = e.currentTarget;
		const manager = button.closest('.xtra-email-manager');
		const authorRows = manager.querySelectorAll('.xtra-subscription-row[data-subscription-type="author"]');
		const communityRows = manager.querySelectorAll('.xtra-subscription-row[data-subscription-type="community"]');
		const totalCount = authorRows.length + communityRows.length;
		const originalText = button.textContent;
		
		if (totalCount === 0) {
			// No subscriptions to remove
			return;
		}
		
		const message = `Are you sure you want to remove all ${totalCount} subscriptions (${authorRows.length} authors, ${communityRows.length} communities)? This cannot be undone.`;
		if (!confirm(message)) {
			return;
		}
		
		button.disabled = true;
		button.textContent = 'Processing...';
		
		try {
			// Unsubscribe from all authors
			let authorMessage = '';
			if (authorRows.length > 0) {
				const headers = {
					'Content-Type': 'application/json',
					'X-WP-Nonce': window.XXRest?.nonce,
				};
				
				const body = {};
				// Add token parameters if present
				if (emailToken && authToken) {
					body.email_token = emailToken;
					body.auth = authToken;
				}
				
				const authorResponse = await fetch('/wp-json/xtra/v1/subscriptions/unsubscribe-all', {
					method: 'POST',
					headers: headers,
					body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined,
				});
				const authorData = await authorResponse.json();
				if (!authorData.success) {
					throw new Error(authorData.message || 'Failed to unsubscribe from authors');
				}
				authorMessage = authorData.message || '';
			}
			
			// Unsubscribe from all communities
			if (communityRows.length > 0) {
				// We need to unsubscribe from each community individually
				for (const row of communityRows) {
					const targetId = row.dataset.targetId;
					const body = { community_id: parseInt(targetId) };
					
					// Add token parameters if present
					if (emailToken && authToken) {
						body.email_token = emailToken;
						body.auth = authToken;
					}
					
					await fetch('/wp-json/xtra/v1/subscriptions/communities/toggle', {
						method: 'POST',
						headers: {
							'Content-Type': 'application/json',
							'X-WP-Nonce': window.XXRest?.nonce,
						},
						body: JSON.stringify(body),
					});
				}
			}
			
			const communityCount = communityRows.length;
			const pieces = [];
			if (authorMessage) {
				pieces.push(authorMessage);
			}
			if (communityCount > 0) {
				pieces.push(`Removed ${communityCount} communit${communityCount === 1 ? 'y' : 'ies'}`);
			}
			showUnsubscribeAllToast(pieces, totalCount);

			// Remove all subscription rows with animation
			const allRows = [...authorRows, ...communityRows];
			allRows.forEach((row, index) => {
				setTimeout(() => {
					row.style.opacity = '0';
					row.style.transform = 'translateX(-20px)';
					setTimeout(() => {
						row.remove();
						if (index === allRows.length - 1) {
							updateSectionCounts();
							checkEmptyState();
						}
					}, 300);
				}, index * 50); // Staggered animation
			});
		} catch (error) {
			console.error('Unsubscribe all error:', error);
			showToast(error.message || 'We could not remove all subscriptions. Please try again.', 'error');
		} finally {
			button.disabled = false;
			button.textContent = originalText;
		}
	}

	function moveToSubscriptions(card) {
		const manager = card.closest('.xtra-email-manager');
		if (!manager) {
			return;
		}

		const type = card.dataset.subscriptionType || 'author';
		const list = manager.querySelector(`.xtra-email-manager__list[data-list-type="${type}"]`);
		if (list) {
			list.appendChild(card);
		}
	}

	function checkEmptyState() {
		document.querySelectorAll('.xtra-email-manager').forEach((manager) => {
			const rows = manager.querySelectorAll('.xtra-subscription-row');
			const emptyState = manager.querySelector('[data-empty-state]');
			if (emptyState) {
				emptyState.style.display = rows.length === 0 ? 'block' : 'none';
			}

			['author', 'community'].forEach((type) => {
				const list = manager.querySelector(`.xtra-email-manager__list[data-list-type="${type}"]`);
				const note = manager.querySelector(`.xtra-email-manager__empty-note[data-empty-note="${type}"]`);
				if (!list || !note) {
					return;
				}
				const hasItems = list.querySelectorAll('.xtra-subscription-row').length > 0;
				note.style.display = hasItems ? 'none' : 'block';
			});
		});
	}
	
	function updateSectionCounts() {
		document.querySelectorAll('.xtra-email-manager').forEach((manager) => {
			const sectionMap = {
				author: 'authors',
				community: 'communities',
			};

			Object.entries(sectionMap).forEach(([type, sectionKey]) => {
				const list = manager.querySelector(`.xtra-email-manager__list[data-list-type="${type}"]`);
				const section = manager.querySelector(`.xtra-email-manager__section[data-section="${sectionKey}"]`);
				if (!list || !section) {
					return;
				}

				const count = list.querySelectorAll('.xtra-subscription-row').length;
				const countEl = section.querySelector('.xtra-email-manager__count');
				if (countEl) {
					countEl.textContent = count;
				}
			});
		});
	}

	function showToast(message, type = 'info') {
		// Use XtraXtra toast notifications if available
		if (window.XtraXtraToast && typeof window.XtraXtraToast.show === 'function') {
			window.XtraXtraToast.show(message, type);
			return;
		}
		
		// Fallback: dispatch custom event for toast system
		const event = new CustomEvent('xtraxtra:notify', {
			detail: { message, type }
		});
		document.dispatchEvent(event);
		
		// Final fallback to console
		console.log(`[${type}] ${message}`);
	}

	function appendSubscriptionRow(manager, subscriptionType, data) {
		if (!manager || !data || Number.isNaN(Number(data.id))) {
			return;
		}

		const list = manager.querySelector(`.xtra-email-manager__list[data-list-type="${subscriptionType}"]`);
		if (!list) {
			return;
		}

		const existingRow = list.querySelector(`.xtra-subscription-row[data-target-id="${data.id}"]`);
		if (existingRow) {
			existingRow.classList.add('xtra-subscription-row--flash');
			setTimeout(() => existingRow.classList.remove('xtra-subscription-row--flash'), 600);
			return;
		}

		const normalizedData = {
			...data,
			emailEnabled: data.emailEnabled !== undefined ? !!data.emailEnabled : true,
			feedEnabled: data.feedEnabled !== undefined ? !!data.feedEnabled : true,
		};

		const row = createSubscriptionRowElement(subscriptionType, normalizedData);
		if (!row) {
			return;
		}

		if (list.firstChild) {
			list.insertBefore(row, list.firstChild);
		} else {
			list.appendChild(row);
		}

		const unsubscribeButton = row.querySelector('.xtra-unsubscribe-button');
		if (unsubscribeButton) {
			unsubscribeButton.addEventListener('click', handleUnsubscribe);
		}

		const emailToggle = row.querySelector('.xtra-email-toggle-input');
		if (emailToggle) {
			emailToggle.addEventListener('change', handleEmailToggle);
		}

		const feedToggle = row.querySelector('.xtra-feed-toggle-input');
		if (feedToggle) {
			feedToggle.addEventListener('change', handleFeedToggle);
		}

		updateSectionCounts();
		checkEmptyState();

		row.style.opacity = '0';
		row.style.transform = 'translateY(-10px)';
		requestAnimationFrame(() => {
			row.style.transition = 'all 0.3s ease';
			row.style.opacity = '1';
			row.style.transform = 'translateY(0)';
		});
	}

	function createSubscriptionRowElement(type, data) {
		const numericId = Number(data.id);
		if (!Number.isFinite(numericId)) {
			return null;
		}

		const safeId = Math.trunc(numericId);
		const name = escapeHtml(data.name || '');
		const postCountLabel = escapeHtml(formatPostCount(data.postCount));
		const emailEnabled = data.emailEnabled !== undefined ? !!data.emailEnabled : true;
		const feedEnabled = data.feedEnabled !== undefined ? !!data.feedEnabled : true;
		let innerHtml = '';

		if (type === 'community') {
			innerHtml = `
				<div class="xtra-subscription-row__main">
					<div class="xtra-subscription-row__info">
						<div class="xtra-subscription-row__community-icon">
							<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
								<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
								<circle cx="12" cy="10" r="3" />
							</svg>
						</div>
						<div class="xtra-subscription-row__details">
							<h4 class="xtra-subscription-row__name">${name}</h4>
							<p class="xtra-subscription-row__posts">${postCountLabel}</p>
						</div>
					</div>
					<button class="xtra-subscription-row__button xtra-unsubscribe-button"
						data-subscription-type="community"
						data-target-id="${safeId}"
						data-target-name="${name}">
						Unfollow
					</button>
				</div>
				<div class="xtra-subscription-row__email-row">
					<label class="xtra-email-digest-toggle">
						<input type="checkbox"
							class="xtra-email-toggle-input"
							${emailEnabled ? 'checked' : ''}
							data-subscription-type="community"
							data-target-id="${safeId}"
							data-target-name="${name}">
						<span class="xtra-checkbox-icon"></span>
						<span class="xtra-email-digest-label">Include in email digest</span>
					</label>
					<label class="xtra-email-digest-toggle">
						<input type="checkbox"
							class="xtra-feed-toggle-input"
							${feedEnabled ? 'checked' : ''}
							data-subscription-type="community"
							data-target-id="${safeId}"
							data-target-name="${name}">
						<span class="xtra-checkbox-icon"></span>
						<span class="xtra-email-digest-label">Include in follower feed</span>
					</label>
				</div>
			`;
		} else {
			const avatar = escapeHtml(data.avatar || 'https://secure.gravatar.com/avatar/?s=96&d=mp');
			innerHtml = `
				<div class="xtra-subscription-row__main">
					<div class="xtra-subscription-row__info">
						<img src="${avatar}" alt="${name}" class="xtra-subscription-row__avatar">
						<div class="xtra-subscription-row__details">
							<h4 class="xtra-subscription-row__name">${name}</h4>
							<p class="xtra-subscription-row__posts">${postCountLabel}</p>
						</div>
					</div>
					<button class="xtra-subscription-row__button xtra-unsubscribe-button"
						data-subscription-type="author"
						data-target-id="${safeId}"
						data-target-name="${name}">
						Unfollow
					</button>
				</div>
				<div class="xtra-subscription-row__email-row">
					<label class="xtra-email-digest-toggle">
						<input type="checkbox"
							class="xtra-email-toggle-input"
							${emailEnabled ? 'checked' : ''}
							data-subscription-type="author"
							data-target-id="${safeId}"
							data-target-name="${name}">
						<span class="xtra-checkbox-icon"></span>
						<span class="xtra-email-digest-label">Include in email digest</span>
					</label>
					<label class="xtra-email-digest-toggle">
						<input type="checkbox"
							class="xtra-feed-toggle-input"
							${feedEnabled ? 'checked' : ''}
							data-subscription-type="author"
							data-target-id="${safeId}"
							data-target-name="${name}">
						<span class="xtra-checkbox-icon"></span>
						<span class="xtra-email-digest-label">Include in follower feed</span>
					</label>
				</div>
			`;
		}

		const row = document.createElement('div');
		row.className = 'xtra-subscription-row';
		if (type === 'community') {
			row.classList.add('xtra-subscription-row--community');
		}
		row.dataset.subscriptionType = type;
		row.dataset.targetId = String(safeId);
		row.innerHTML = innerHtml.trim();

		return row;
	}

	function getSearchConfig(type) {
		if (type === 'community') {
			return {
				endpoint: '/wp-json/xtra/v1/subscriptions/communities/search',
				loading: 'Searching communities...',
				empty: 'No communities found',
				error: 'Error searching communities',
			};
		}

		return {
			endpoint: '/wp-json/xtra/v1/subscriptions/search-authors',
			loading: 'Searching authors...',
			empty: 'No authors found',
			error: 'Error searching authors',
		};
	}

	function formatFollowerMeta(postCount, subscriberCount) {
		return `${formatPostCount(postCount)} • ${formatSubscriberCount(subscriberCount)}`;
	}

	function formatPostCount(count) {
		const numeric = Number(count);
		const safeCount = Number.isFinite(numeric) ? Math.max(0, Math.trunc(numeric)) : 0;
		return safeCount === 1 ? '1 post' : `${safeCount} posts`;
	}

	function formatSubscriberCount(count) {
		const numeric = Number(count);
		const safeCount = Number.isFinite(numeric) ? Math.max(0, Math.trunc(numeric)) : 0;
		return safeCount === 1 ? '1 subscriber' : `${safeCount} subscribers`;
	}

	function escapeHtml(value) {
		return String(value ?? '')
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#039;');
	}

	function showUnsubscribeAllToast(parts, totalCount) {
		const summary = parts.filter(Boolean).join(' · ');
		const fallback = totalCount > 0
			? `Removed ${totalCount} subscription${totalCount === 1 ? '' : 's'}.`
			: 'Removed all subscriptions.';
		showToast(summary || fallback, 'success');
	}

	async function handleEmailListToggle(e) {
		const toggle = e.currentTarget;
		const subscribed = toggle.checked;
		const listKey = toggle.dataset.listKey;
		const listId = parseInt(toggle.dataset.listId || '0', 10);

		if (!listKey && !listId) {
			return;
		}

		toggle.disabled = true;

		try {
			const body = {
				subscribed,
			};

			if (listKey) {
				body.list_key = listKey;
			}

			if (listId) {
				body.list_id = listId;
			}

			if (emailToken && authToken) {
				body.email_token = emailToken;
				body.auth = authToken;
			}

			const response = await fetch('/wp-json/xtra/v1/subscriptions/email-lists/toggle', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-WP-Nonce': window.XXRest?.nonce,
				},
				body: JSON.stringify(body),
			});

			const data = await response.json();

			if (!data.success) {
				toggle.checked = !subscribed;
				showToast(data.message || 'We could not update your preference.', 'error');
			} else {
				showToast(data.message || (subscribed ? 'Email list enabled.' : 'Email list disabled.'), 'success');
			}
		} catch (error) {
			console.error('Email list toggle error:', error);
			toggle.checked = !subscribed;
			showToast('We could not update your preference. Please try again.', 'error');
		} finally {
			toggle.disabled = false;
		}
	}

	async function handleEmailToggle(e) {
		const toggle = e.currentTarget;
		const enabled = toggle.checked;
		const subscriptionType = toggle.dataset.subscriptionType;
		const targetId = toggle.dataset.targetId;
		const targetName = toggle.dataset.targetName;
		
		// Disable during request
		toggle.disabled = true;
		
		try {
			const body = {
				subscription_type: subscriptionType,
				target_id: parseInt(targetId),
				enabled: enabled
			};
			
			// Add token parameters if present
			if (emailToken && authToken) {
				body.email_token = emailToken;
				body.auth = authToken;
			}
			
			const response = await fetch('/wp-json/xtra/v1/subscriptions/toggle-email', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-WP-Nonce': window.XXRest?.nonce,
				},
				body: JSON.stringify(body),
			});
			
			const data = await response.json();
			
			if (data.success) {
				showToast(data.message || `${enabled ? 'Enabled' : 'Disabled'} email updates for ${targetName || subscriptionType}.`, 'success');
			} else {
				// Revert toggle on error
				toggle.checked = !enabled;
				showToast(data.message || 'We could not update your preference.', 'error');
			}
		} catch (error) {
			console.error('Email toggle error:', error);
			// Revert toggle on error
			toggle.checked = !enabled;
			showToast('We could not update your preference. Please try again.', 'error');
		} finally {
			toggle.disabled = false;
		}
	}

	async function handleFeedToggle(e) {
		const toggle = e.currentTarget;
		const enabled = toggle.checked;
		const subscriptionType = toggle.dataset.subscriptionType;
		const targetId = toggle.dataset.targetId;
		const targetName = toggle.dataset.targetName;

		toggle.disabled = true;

		try {
			const body = {
				subscription_type: subscriptionType,
				target_id: parseInt(targetId, 10),
				enabled: enabled,
			};

			if (emailToken && authToken) {
				body.email_token = emailToken;
				body.auth = authToken;
			}

			const response = await fetch('/wp-json/xtra/v1/subscriptions/toggle-feed', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-WP-Nonce': window.XXRest?.nonce,
				},
				body: JSON.stringify(body),
			});

			const data = await response.json();

			if (data.success) {
				const label = subscriptionType === 'community' ? 'community' : 'creator';
				const toastMessage = data.message || (enabled
					? `Added ${targetName || `this ${label}`} to your follower feed.`
					: `Removed ${targetName || `this ${label}`} from your follower feed.`);
				showToast(toastMessage, 'success');
			} else {
				toggle.checked = !enabled;
				showToast(data.message || 'We could not update your follower feed preference.', 'error');
			}
		} catch (error) {
			console.error('Feed toggle error:', error);
			toggle.checked = !enabled;
			showToast('We could not update your follower feed preference. Please try again.', 'error');
		} finally {
			toggle.disabled = false;
		}
	}

	async function handleAutoSubscribeToggle(e) {
		const toggle = e.currentTarget;
		const enabled = toggle.checked;
		
		// Disable during request
		toggle.disabled = true;
		
		try {
			const body = {
				enabled: enabled
			};
			
			// Add token parameters if present
			if (emailToken && authToken) {
				body.email_token = emailToken;
				body.auth = authToken;
			}
			
			const response = await fetch('/wp-json/xtra/v1/subscriptions/auto-subscribe', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-WP-Nonce': window.XXRest?.nonce,
				},
				body: JSON.stringify(body),
			});
			
			const data = await response.json();
			
			if (data.success) {
				showToast(data.message || (enabled ? 'New follows will be added to your digest.' : 'New follows will no longer auto-subscribe.'), 'success');
			} else {
				// Revert toggle on error
				toggle.checked = !enabled;
				showToast(data.message || 'We could not update this preference.', 'error');
			}
		} catch (error) {
			console.error('Auto-subscribe toggle error:', error);
			// Revert toggle on error
			toggle.checked = !enabled;
			showToast('We could not update this preference. Please try again.', 'error');
		} finally {
			toggle.disabled = false;
		}
	}

	/**
	 * Reorganize layout for mobile devices
	 * Moves unfollow button to email row on mobile
	 */
	function reorganizeForMobile() {
		// Only apply on mobile
		if (window.innerWidth > 640) return;

		const subscriptionRows = document.querySelectorAll('.xtra-subscription-row');

		subscriptionRows.forEach(row => {
			const mainButton = row.querySelector('.xtra-subscription-row__main .xtra-subscription-row__button');
			const emailRow = row.querySelector('.xtra-subscription-row__email-row');

			if (mainButton && emailRow) {
				// Clone the button for mobile
				const mobileButton = mainButton.cloneNode(true);
				mobileButton.classList.add('xtra-subscription-row__button-mobile');

				// Add event listener to the cloned button
				mobileButton.addEventListener('click', handleUnsubscribe);

				// Append to email row
				emailRow.appendChild(mobileButton);
			}
		});
	}

	// Re-organize on window resize
	let resizeTimeout;
	window.addEventListener('resize', () => {
		clearTimeout(resizeTimeout);
		resizeTimeout = setTimeout(() => {
			// Remove any existing mobile buttons
			document.querySelectorAll('.xtra-subscription-row__button-mobile').forEach(btn => btn.remove());
			// Re-apply mobile layout if needed
			reorganizeForMobile();
		}, 250);
	});
})();
